import {
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  ViewChild,
} from '@angular/core';
import { FormFieldService } from '../../../services/form-field/form-field.service';
import {
  AutocompleteFormFieldOptions,
  FormFieldLabels,
} from '../../../form-field.types';
import { AbstractControl, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatIconModule } from '@angular/material/icon';
import {
  MatAutocompleteModule,
  MatAutocompleteTrigger,
} from '@angular/material/autocomplete';
import { combineLatestWith, concat, of, switchMap } from 'rxjs';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { ErrorStateMatcher } from '@angular/material/core';

/*
Implementation notes
====================

Why only labels are used and internal values for options are not
----------------------------------------------------------------

If `value`s are objects, setValue is difficult.

If `value`s are strings, `valueChanges` emits them together with user input and it's not easy to tell apart user input from internal values.

This component considers autocomplete an extension of a text input rather than select.
*/

@Component({
  selector: 'app-autocomplete-form-field',
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatTooltipModule,
    MatIconModule,
    MatAutocompleteModule,
    MatAutocompleteTrigger,
  ],
  templateUrl: './autocomplete-form-field.component.html',
  styleUrl: './autocomplete-form-field.component.scss',
})
export class AutocompleteFormFieldComponent {
  private formFieldService = inject(FormFieldService);

  @ViewChild('htmlInput')
  htmlInput?: ElementRef<HTMLInputElement>;

  name = input('');
  labels = input<FormFieldLabels>();
  options = input<AutocompleteFormFieldOptions>();
  path = input<string>();
  errorStateMatcher = input<ErrorStateMatcher>({
    isErrorState: control => !!control?.errors,
  });

  formGroup = this.formFieldService.formGroup;
  private formGroup$ = this.formFieldService.formGroup$;

  label = computed(() => this.labels()?.label);
  placeholder = computed(() => this.labels()?.placeholder ?? '');
  help = computed(() => this.labels()?.help);
  error = this.formFieldService.controlErrorMessage(this.name, this.path);
  required = computed(() => !!this.options()?.required);

  predicate = computed(
    () => this.options()?.predicate ?? this.defaultPredicate,
  );

  private autocompleteOptions = computed(() =>
    (this.labels()?.options ?? []).map(option => option.value),
  );

  private control = toSignal(
    this.formGroup$.pipe(
      combineLatestWith(toObservable(this.name)),
      switchMap(([formGroup, name]) => {
        const control = formGroup?.get(name);
        return control ? of(control) : of();
      }),
    ),
  );

  private controlValue = toSignal(
    concat(
      of(''),
      this.formGroup$.pipe(
        combineLatestWith(toObservable(this.name)),
        switchMap(([formGroup, name]) => {
          if (!formGroup) {
            return of();
          }
          const control = formGroup.controls[name];
          if (!control) {
            return of();
          }
          return control.valueChanges.pipe(
            switchMap(value => {
              if (typeof value === 'string') {
                return of(value);
              }
              if (value === undefined || value === null) {
                return of('');
              }
              return of();
            }),
          );
        }),
      ),
    ),
  );

  filteredOptions = computed(() => {
    const autocompleteOptions = this.autocompleteOptions();
    const predicate = this.predicate();
    const controlValue = this.controlValue() || '';
    return autocompleteOptions.filter(option =>
      predicate(controlValue, option),
    );
  });

  private defaultPredicate = (controlValue: string, option: string) =>
    option.toLowerCase().includes(controlValue.toLowerCase());

  private configureControl = computed(() => (control: AbstractControl) => {
    this.options()?.configureControl?.(control);
    if (this.options()?.strict) {
      this.addInListValidator(control);
    }
  });

  private addInListValidator(control: AbstractControl) {
    control.addValidators(cntrl => {
      const value = cntrl.value;
      if (!value) {
        return null;
      }
      if (this.filteredOptions().includes(value)) {
        return null;
      }
      return { invalidCompletion: true };
    });
  }

  private configureControlEffect = () => {
    const configureControl = this.configureControl();
    const control = this.control();
    if (control && configureControl) {
      configureControl(control);
    }
  };

  constructor() {
    effect(this.configureControlEffect);
  }
}
